import type { DriverTarget } from "@skaile/workspaces/plugin-registry";
import type { Logger } from "@skaile/workspaces/types";
import * as z from "zod";
import { getBridgeLogger } from "../logger.js";
import type { ModelEntry } from "../models.js";
import { BUILTIN_DRIVER_CATALOG } from "../registry.js";
import type { AgentConfig, DriverInfo } from "../types.js";
import {
  extractCost,
  extractText,
  extractTokens,
  mapApiKeysToEnv,
  SubprocessCliDriver,
} from "./_subprocess-cli.js";

/**
 * Agent driver for the **Continue CLI** (`cn`), the model-agnostic agent backed
 * by Continue Hub / `config.yaml` assistants.
 *
 * Unlike the other CLI drivers, `cn -p "<prompt>" --format json` emits a single
 * **final-result JSON wrapper**, not a streaming event log. This driver runs in
 * `mode: "buffered"`: the base accumulates all stdout and {@link handleBuffered}
 * parses it once on exit — so there are no incremental deltas or per-tool
 * events, only a final assistant message plus a result.
 *
 * @remarks
 * - Auth via `CONTINUE_API_KEY` / `cn login`; provider keys flow through the
 *   environment and the selected `config.yaml`.
 * - `--config <slug>` selects the assistant; session resume via `--resume`
 *   (last session only — Continue has no resume-by-id in headless mode).
 *
 * @see https://docs.continue.dev/guides/cli
 * @docLink packages/bridge/drivers#continue-driver
 */
export class ContinueDriver extends SubprocessCliDriver {
  readonly driverInfo: DriverInfo = {
    id: "continue",
    name: "Continue CLI",
    modelAgnostic: true,
    supportsInBandAbort: false,
  };

  protected readonly log: Logger = getBridgeLogger("continue");
  protected readonly defaultBin = "cn";
  protected readonly productName = "Continue CLI";
  protected readonly envPrefix = "CONTINUE";
  protected override readonly mode = "buffered" as const;

  protected buildArgs(message: string, resumeId: string | undefined): string[] {
    const args = ["-p", message, "--format", "json", "--silent"];
    const slug = this.config.model; // Continue selects assistants/models by config slug.
    if (slug) args.push("--config", slug);
    if (resumeId) args.push("--resume");
    return args;
  }

  protected buildEnv(): Record<string, string> {
    const env = this.baseEnv(["CONTINUE_API_KEY"]);
    const key =
      this.config.apiKeys?.continue ??
      this.config.env?.CONTINUE_API_KEY ??
      process.env.CONTINUE_API_KEY;
    if (key) env.CONTINUE_API_KEY = key;
    return { ...env, ...mapApiKeysToEnv(this.config.apiKeys) };
  }

  protected installHint(): string {
    return "Continue CLI is not installed or not on your PATH. Install it with `npm i -g @continuedev/cli`.";
  }

  protected handleBuffered(raw: string): void {
    const json = parseLastJsonObject(raw);
    if (!json) {
      // No structured wrapper — surface the raw stdout as the assistant reply.
      const text = raw.trim();
      if (text) {
        this.pushAssistantDelta(text);
        this.pushAssistantEnd(text);
      }
      this.pushResult({ summary: text || "Task completed" });
      return;
    }
    this.captureSession(json.sessionId ?? json.session_id ?? json.id);
    const text = extractText(json) || (typeof json.output === "string" ? json.output : "");
    if (text) {
      this.pushAssistantDelta(text);
      this.pushAssistantEnd(text);
    }
    this.pushResult({
      tokens: extractTokens(json),
      costUsd: extractCost(json),
      summary: text || "Task completed",
    });
  }

  override async listModels(): Promise<ModelEntry[]> {
    return [];
  }
}

/**
 * Parse the last balanced top-level JSON object in `raw`. Continue may print
 * log lines before the final `--format json` blob; we want the wrapper, which
 * is the last `{...}` in the output.
 */
function parseLastJsonObject(raw: string): any | null {
  const end = raw.lastIndexOf("}");
  if (end < 0) return null;
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let i = end; i >= 0; i--) {
    const ch = raw[i];
    if (inString) {
      if (escaped) escaped = false;
      else if (ch === "\\") escaped = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') inString = true;
    else if (ch === "}") depth++;
    else if (ch === "{") {
      depth--;
      if (depth === 0) {
        try {
          return JSON.parse(raw.slice(i, end + 1));
        } catch {
          return null;
        }
      }
    }
  }
  return null;
}

/**
 * `DriverTarget` wrapper for the continue driver. Registered into the plugin
 * registry by `registerBuiltinDrivers()`.
 */
export const continueDriverTarget: DriverTarget<AgentConfig> = {
  id: "continue",
  displayName: BUILTIN_DRIVER_CATALOG.continue?.name ?? "Continue CLI",
  apiVersion: 1,
  configSchema: z.custom<AgentConfig>(() => true),
  create: async (config, _ctx) => new ContinueDriver(config),
};
